
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SEO } from './SEO';
import {
  TrendingUp,
  Shield,
  Globe,
  Star,
  Brain,
  Users,
  CheckCircle,
  Building,
  Languages,
  MessageCircle,
  BookOpen,
  Award,
  Target,
  Mail,
  Phone
} from 'lucide-react';
import Beams from './Beams';

const SarvalokForInvestors: React.FC = () => {
  const { t } = useTranslation(['invest', 'seo']);
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <SEO
        title={t('seo:invest.title', { defaultValue: 'Investment Opportunities | Sarvalok AI Platform' })}
        description={t('seo:invest.description', { defaultValue: "Discover investment opportunities in the future of cultural AI. Join Sarvalok's mission to preserve and democratize access to ancient wisdom through cutting-edge technology." })}
        url="/for-investor"
      />
      {/* Academic Header with Beams Effect */}
      <div className="relative overflow-hidden" style={{ height: '320px' }}>
        {/* Beams Background */}
        <div className="absolute inset-0">
          <Beams
            beamWidth={3}
            beamHeight={30}
            beamNumber={20}
            lightColor="#ffffff"
            speed={2}
            noiseIntensity={1.75}
            scale={0.2}
            rotation={30}
          />
        </div>
        
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black opacity-40"></div>

        <div className="relative px-8 lg:px-20 py-12 text-white">
          <div className="max-w-5xl">
            <div className="flex items-center mb-6 space-x-3">
              <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
                <Award className="w-6 h-6 text-amber-300" />
              </div>
              <span className="text-amber-200 font-medium tracking-wide text-sm uppercase">{t('invest.header_badge')}</span>
            </div>
            
            <h1 className="text-3xl lg:text-5xl font-bold mb-6 leading-tight text-white">
              {t('invest.header_title')}
            </h1>
            <p className="text-lg lg:text-xl text-slate-200 leading-relaxed max-w-3xl">
              {t('invest.header_subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="px-8 lg:px-20 py-10">
        {/* Executive Summary */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="grid lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2 space-y-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {t('invest.executive_summary_title')}
              </h2>
              <p className="text-gray-700 leading-relaxed">
                {t('invest.executive_summary_paragraph1')}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {t('invest.executive_summary_paragraph2')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('invest.global_impact_title')}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t('invest.global_impact_description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Market Opportunity */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-3">{t('invest.market_opportunity_title')}</h2>
              <div className="w-16 h-0.5 bg-blue-600 mx-auto"></div>
            </div>

            <div className="prose max-w-4xl mx-auto">
              <p className="text-gray-700 leading-relaxed mb-6">
                {t('invest.market_opportunity_description')}
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 my-8">
                 <div className="bg-white rounded-sm p-8 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                   <div className="border-l-4 border-slate-600 pl-4 mb-4">
                     <h3 className="text-lg font-semibold text-slate-800 mb-1">{t('invest.faith_tech_title')}</h3>
                     <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">{t('invest.faith_tech_subtitle')}</div>
                   </div>
                   <p className="text-slate-700 leading-relaxed text-sm">
                     {t('invest.faith_tech_description')}
                   </p>
                 </div>
                 
                 <div className="bg-white rounded-sm p-8 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                   <div className="border-l-4 border-slate-600 pl-4 mb-4">
                     <h3 className="text-lg font-semibold text-slate-800 mb-1">{t('invest.wellness_spending_title')}</h3>
                     <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">{t('invest.wellness_spending_subtitle')}</div>
                   </div>
                   <p className="text-slate-700 leading-relaxed text-sm">
                     {t('invest.wellness_spending_description')}
                   </p>
                 </div>
               </div>

              <p className="text-gray-700 leading-relaxed">
                {t('invest.market_need_description')}
              </p>
            </div>
          </div>
        </div>

        {/* Investment Thesis */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-3">{t('invest.investment_thesis_title')}</h2>
            <div className="w-16 h-0.5 bg-blue-600 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <TrendingUp className="w-6 h-6" />,
                title: t('invest.massive_market_title'),
                description: t('invest.massive_market_description'),
                color: "blue"
              },
              {
                icon: <Shield className="w-6 h-6" />,
                title: t('invest.technical_advantage_title'),
                description: t('invest.technical_advantage_description'),
                color: "purple"
              },
              {
                icon: <Star className="w-6 h-6" />,
                title: t('invest.first_mover_title'),
                description: t('invest.first_mover_description'),
                color: "indigo"
              },
              {
                icon: <Users className="w-6 h-6" />,
                title: t('invest.scalable_business_title'),
                description: t('invest.scalable_business_description'),
                color: "green"
              },
              {
                icon: <CheckCircle className="w-6 h-6" />,
                title: t('invest.problem_resolution_title'),
                description: t('invest.problem_resolution_description'),
                color: "orange"
              },
              {
                icon: <Globe className="w-6 h-6" />,
                title: t('invest.social_impact_title'),
                description: t('invest.social_impact_description'),
                color: "violet"
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-100">
                <div className={`w-10 h-10 bg-${item.color}-100 rounded-lg flex items-center justify-center mb-4`}>
                  <div className={`text-${item.color}-600`}>
                    {item.icon}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
        {/* Competitive Advantages */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="bg-slate-50 rounded-xl p-8 border border-gray-100">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-3">{t('invest.competitive_advantages_title')}</h2>
              <div className="w-16 h-0.5 bg-slate-600 mx-auto"></div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  icon: <Brain className="w-5 h-5" />,
                  title: t('invest.heritage_ai_title'),
                  description: t('invest.heritage_ai_description')
                },
                {
                  icon: <Shield className="w-5 h-5" />,
                  title: t('invest.authenticity_title'),
                  description: t('invest.authenticity_description')
                },
                {
                  icon: <Building className="w-5 h-5" />,
                  title: t('invest.source_access_title'),
                  description: t('invest.source_access_description')
                },
                {
                  icon: <Users className="w-5 h-5" />,
                  title: t('invest.expert_team_title'),
                  description: t('invest.expert_team_description')
                },
                {
                  icon: <Target className="w-5 h-5" />,
                  title: t('invest.market_gap_title'),
                  description: t('invest.market_gap_description')
                }
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="text-slate-600">
                      {item.icon}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
              
              {/* Third-Party Validation by Google for Startups */}
              <div className="md:col-span-2 mt-6">
                <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                  <div className="flex items-start space-x-4">
                    <div className="p-2 bg-blue-50 rounded-lg shadow-sm border border-blue-100">
                      <Award className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('invest.google_validation_title')}</h3>
                      <div className="mb-3">
                         <img 
                           src="/StartupProgram.png" 
                           alt="Google for Startups Cloud Program" 
                           className="h-11 w-auto"
                         />
                       </div>
                      <p className="text-gray-600 leading-relaxed">
                        {t('invest.google_validation_description')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Growth Strategy */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-3">{t('invest.growth_strategy_title')}</h2>
              <div className="w-16 h-0.5 bg-blue-600 mx-auto"></div>
            </div>

            <div className="prose max-w-4xl mx-auto text-center">
              <p className="text-gray-700 leading-relaxed mb-6">
                {t('invest.growth_strategy_description')}
              </p>
              
              <div className="bg-blue-50 rounded-lg p-6 my-6">
                <p className="text-gray-700 leading-relaxed">
                  {t('invest.mission_statement')}
                </p>
              </div>

              <p className="text-gray-700 leading-relaxed">
                {t('invest.business_model_description')}
              </p>
            </div>
          </div>
        </div>

        {/* Value Proposition */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-3">{t('invest.unique_value_proposition_title')}</h2>
            <div className="w-16 h-0.5 bg-blue-600 mx-auto"></div>
          </div>

          <div className="space-y-6">
            {[
              {
                icon: <TrendingUp className="w-6 h-6" />,
                title: t('invest.specialized_focus_title'),
                description: t('invest.specialized_focus_description'),
                color: "blue"
              },
              {
                icon: <Brain className="w-6 h-6" />,
                title: t('invest.superior_technology_title'),
                description: t('invest.superior_technology_description'),
                color: "purple"
              },
              {
                icon: <Languages className="w-6 h-6" />,
                title: t('invest.global_reach_title'),
                description: t('invest.global_reach_description'),
                color: "green"
              },
              {
                icon: <Globe className="w-6 h-6" />,
                title: t('invest.mission_driven_title'),
                description: t('invest.mission_driven_description'),
                color: "violet"
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 bg-${item.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <div className={`text-${item.color}-600`}>
                      {item.icon}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Information */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-slate-800 rounded-xl overflow-hidden">
            <div className="p-8 text-white text-center">
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-3">{t('invest.investment_inquiries_title')}</h2>
                <div className="w-16 h-0.5 bg-amber-400 mx-auto mb-4"></div>
                <p className="text-slate-200 leading-relaxed max-w-2xl mx-auto">
                  {t('invest.investment_inquiries_description')}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 max-w-2xl mx-auto">
                <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-5 h-5 text-amber-300" />
                  </div>
                  <h3 className="font-semibold mb-2 text-sm">{t('invest.contact_email_label')}</h3>
                  <a href="mailto:invest@sarvalok.com" className="text-amber-300 hover:text-amber-200 transition-colors text-sm">
                    invest@sarvalok.com
                  </a>
                </div>

                <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Phone className="w-5 h-5 text-amber-300" />
                  </div>
                  <h3 className="font-semibold mb-2 text-sm">{t('invest.contact_phone_label')}</h3>
                  <p className="text-amber-300 text-sm">+91 8125831103</p>
                </div>

                <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <MessageCircle className="w-5 h-5 text-green-300" />
                  </div>
                  <h3 className="font-semibold mb-2 text-sm">{t('invest.contact_whatsapp_label')}</h3>
                  <a 
                    href="https://wa.me/918125831103?text=Hello%2C%20I%20have%20a%20question%20about%20Sarvalok.%20Can%20you%20please%20help%20me%3F" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-green-300 hover:text-green-200 transition-colors text-sm inline-flex items-center justify-center"
                  >
                    {t('invest.contact_whatsapp_action')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
};

export default SarvalokForInvestors;
